import { Component } from 'preact';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import CSSTransition from 'react-transition-group/CSSTransition';
import classNames from 'classnames';

// Utils
import {
  calcColumns,
  stepHasRequiredData,
  getStepWaitText,
  isLoadingOnField,
  stepShowHide,
  stepToggleType,
} from '../../../utilities/form/formUtils';

// Constants
import { stepComponents } from '../../../constants/Steps';

// Components
import FormActions from '../../Form/FormActions';
import StepHeader from '../General/StepHeader';
import Text from '../../Global/Text';
import { getStepTitle, triggerView } from '../../../utilities/adobeTarget';
import { replaceAll } from '../../../utilities/common';
import Divider from '../General/Divider';

class Basic extends Component {
  componentDidMount() {
    const viewName = getStepTitle();
    if (viewName) {
      triggerView(viewName);
    }
  }

  render() {
    const {
      componentProps,
      title,
      desc,
      submitText,
      submitButton,
      dispatch,
      errors,
      handleSubmit,
      onBlur,
      onChange,
      setValues,
      touched,
      values,
      tracking,
      setFormValues,
      formActions,
      requiredData,
      userData,
      actionProps,
      actionVariant,
      initialValues,
      setFieldValue,
      className,
      submitIcon,
      applyFormSubmitClass,
      hidden,
      showHide,
      afterButtonText,
      activeSectionData,
      currentProgress,
      hideNext,
      buttonPostClass,
      errorCodeDisplay,
      formClassName,
      variant,
      formImage,
      translations,
      nextButtonType,
      nextButtonLinkUrl,
      nextButtonDivClassName,
      logo,
      logoTitle,
      showLogo
    } = this.props;

    const formParams = {
      handleSubmit,
      onChange,
      onBlur,
      errors,
      touched,
      setValues,
      values,
      setFormValues,
      dispatch,
      userData,
      setFieldValue,
      initialValues,
    };

    if (stepShowHide(hidden, showHide, userData, values)) {
      return <div />;
    }
    const formVariant = variant === 'twoColumnWithImage';
    const leadFormVariant = classNames(
      {
        'section-left': formVariant,
      }
    );
    const formImagePath = replaceAll(formImage, translations, userData);

    return (
      <article className={`step step--basic ${className}`}>
        {showLogo && 
          <>
            <Divider className='basic_component_divider'/>
            <div className='basic_component_wrapper'>
              <Text className='basic_component_wrapper_logo' value={logo} /> 
              <Text className='basic_component_wrapper_desc' value={logoTitle}/>
            </div>
          </>
        }
        <div className={leadFormVariant}>
          {(title || desc) && (
            <StepHeader title={title} desc={desc} />
          )}

          {errorCodeDisplay && (
            <Text className="text__desc--after-button" value={errorCodeDisplay} />
          )}

          {/* If there's any props */}
          {componentProps.length > 0 && (
            <div className="step__content">
              {componentProps.map(componentProp => {
                const { loadingOnField } = componentProp;
                const hiddenLocal = componentProp.hidden ? componentProp.hidden : false;
                const showHideLocal = componentProp.showHide ? componentProp.showHide : null;
                const toggleType = componentProp.toggleType ? componentProp.toggleType : null;
                const inputParams = {
                  ...userData,
                  ...componentProp,
                  ...formParams,
                  isLoading: loadingOnField ? isLoadingOnField(loadingOnField, userData) : null,
                  'columns': calcColumns(componentProp),
                  'children': componentProps,
                  hidden: (stepShowHide(hiddenLocal, showHideLocal, userData, values)),
                  toggleType: (stepToggleType(toggleType, userData, values)),
                  currentProgress
                };

                const InputComponent = stepComponents[componentProp.type];
                if (InputComponent) {
                  return (
                    <CSSTransition
                      in={inputParams.hidden !== true}
                      timeout={300}
                      classNames="slideInTop"
                      unmountOnExit
                    >
                      <InputComponent {...inputParams} />
                    </CSSTransition>
                  );
                }
              })}

              {/* Actions Button */}
              {submitButton &&
                <FormActions
                  formActions={formActions}
                  submitText={submitText}
                  submitButton={submitButton}
                  isValid={stepHasRequiredData(requiredData, userData, values)}
                  dispatch={dispatch}
                  values={values}
                  waitText={getStepWaitText(requiredData, userData, values)}
                  tracking={tracking}
                  formParams={formParams}
                  actionProps={actionProps}
                  userData={userData}
                  actionVariant={actionVariant}
                  setFieldValue={setFieldValue}
                  submitIcon={submitIcon}
                  applyFormSubmitClass={applyFormSubmitClass}
                  activeSectionData={activeSectionData}
                  currentProgress={currentProgress}
                  hideNext={hideNext}
                  buttonPostClass={buttonPostClass}
                  formClassName={formClassName}
                  nextButtonType={nextButtonType}
                  nextButtonLinkUrl={nextButtonLinkUrl}
                  nextButtonDivClassName={nextButtonDivClassName}
                />
              }

              {afterButtonText &&
                <p className="text__desc--after-button">
                  <Text value={afterButtonText} />
                </p>
              }

            </div>
          )}
        </div>
        {formVariant &&
          <MediaQuery minWidth={769}>
            <div className='section-right'>
              <img src={formImagePath} alt="product" />
            </div>
          </MediaQuery>
        }
        {showLogo && <Divider className='basic_component_divider'/>}
      </article>
    );
  }
}

export default Basic;

Basic.defaultProps = {
  actionProps: null,
  desc: null,
  errors: null,
  requiredData: {},
  submitButton: true,
  title: null,
  tracking: ['tealiumSubmit'],
  actionVariant: 'greyBox',
  className: '',
  submitIcon: true,
  applyFormSubmitClass: true,
  hidden: false,
  showHide: null,
  afterButtonText: '',
  hideNext: false,
  buttonPostClass: '',
  errorCodeDisplay: null,
  formClassName: '',
  formImage: '',
  variant: '',
  logo: "${tryg-logo-size-fina}",
  logoTitle: "${tryg-logo-title-fina}",
  showLogo: false
};

Basic.propTypes = {
  componentProps: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  submitText: PropTypes.string.isRequired,
  actionProps: PropTypes.array,
  desc: PropTypes.string,
  actionVariant: PropTypes.string,
  errors: PropTypes.object,
  formActions: PropTypes.array,
  tracking: PropTypes.array,
  handleSubmit: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  requiredData: PropTypes.object,
  setFormValues: PropTypes.func,
  setValues: PropTypes.func,
  submitButton: PropTypes.bool,
  title: PropTypes.string,
  touched: PropTypes.object,
  userData: PropTypes.object,
  values: PropTypes.object,
  initialValues: PropTypes.object,
  setFieldValue: PropTypes.func,
  className: PropTypes.string,
  submitIcon: PropTypes.bool,
  applyFormSubmitClass: PropTypes.bool,
  hidden: PropTypes.bool,
  showHide: PropTypes.object,
  afterButtonText: PropTypes.string,
  activeSectionData: PropTypes.object,
  currentProgress: PropTypes.object.isRequired,
  hideNext: PropTypes.bool,
  buttonPostClass: PropTypes.string,
  errorCodeDisplay: PropTypes.string,
  formClassName: PropTypes.string,
  formImage: PropTypes.string,
  variant: PropTypes.string,
  translations: PropTypes.object,
  nextButtonType: PropTypes.string,
  nextButtonLinkUrl: PropTypes.string,
  nextButtonDivClassName: PropTypes.string,
  logo: PropTypes.string,
  logoTitle: PropTypes.string,
  showLogo: PropTypes.bool
};
